import { z } from "zod";

import { PYTHON_ID_CONTINUE, PYTHON_ID_START } from "./generated.js";

// https://docs.python.org/3.10/reference/lexical_analysis.html#identifiers
// to give slightly better error messages, we first check if the name matches
// `id_continue+`, then check if the first character matches `id_start`
const ID_CHARS_REGEX = new RegExp(`^${PYTHON_ID_CONTINUE}+$`, "u");
const BAD_START_CLASS = PYTHON_ID_START.replace("[", "[^");
const BAD_START_REGEX = new RegExp(`^(${BAD_START_CLASS})`, "u");

// https://docs.python.org/3.10/reference/lexical_analysis.html#keywords
// or `keyword.kwlist` in Python
const KEYWORDS = new Set([
  "False",
  "None",
  "True",
  "and",
  "as",
  "assert",
  "async",
  "await",
  "break",
  "class",
  "continue",
  "def",
  "del",
  "elif",
  "else",
  "except",
  "finally",
  "for",
  "from",
  "global",
  "if",
  "import",
  "in",
  "is",
  "lambda",
  "nonlocal",
  "not",
  "or",
  "pass",
  "raise",
  "return",
  "try",
  "while",
  "with",
  "yield",
]);

export const PythonIdentifier = z
  .string()
  .brand("PythonIdentifier")
  // `superRefine`, not `refine`: the runtypes constraint returned a different
  // message per branch, and `refine` has one message for the whole predicate --
  // worse, it treats a returned string as `true`, so the check would not run.
  .superRefine((name, ctx) => {
    // https://docs.python.org/3.10/reference/lexical_analysis.html#identifiers
    // > All identifiers are converted into the normal form NFKC while parsing;
    // > comparison of identifiers is based on NFKC.
    const normalized = name.normalize("NFKC");

    const fail = (message: string): void => {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    };

    if (KEYWORDS.has(normalized)) {
      fail(`'${name}' is a reserved Python keyword`);
      return;
    }

    if (!ID_CHARS_REGEX.test(normalized)) {
      fail(`'${name}' contains invalid characters for a Python identifier`);
      return;
    }

    const badStart = BAD_START_REGEX.exec(normalized);
    if (badStart) {
      fail(`'${name}' cannot start with character ${badStart[1]}`);
    }
  });
